import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import { FormSection, FieldRow, controlClass } from '../../components/FormSection';
import { Button } from '../../components/Button';
import {
  useDeleteTemplate,
  useMarketingEligibility,
  useMarketingTemplates,
  useSaveTemplate,
  useSendMarketing,
  type MarketingTemplate,
  type UpsertTemplate
} from './hooks';

type View =
  | { kind: 'list' }
  | { kind: 'edit'; template?: MarketingTemplate }
  | { kind: 'send'; template: MarketingTemplate };

const REASON_LABEL: Record<string, string> = {
  no_email: 'No email on file',
  unsubscribed: 'Unsubscribed'
};

export function MarketingPage() {
  usePageTitle('Marketing');
  const [view, setView] = useState<View>({ kind: 'list' });

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-5">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink">Marketing</h1>
        <p className="text-slate-500 text-sm mt-1">
          Send an update to your clients. Every email includes your business address and a one-click
          unsubscribe — automatically, as the law requires.
        </p>
      </div>

      {view.kind === 'list' && (
        <TemplateList
          onNew={() => setView({ kind: 'edit' })}
          onEdit={(t) => setView({ kind: 'edit', template: t })}
          onSend={(t) => setView({ kind: 'send', template: t })}
        />
      )}
      {view.kind === 'edit' && (
        <TemplateEditor template={view.template} onDone={() => setView({ kind: 'list' })} />
      )}
      {view.kind === 'send' && (
        <SendPanel template={view.template} onDone={() => setView({ kind: 'list' })} />
      )}
    </div>
  );
}

function TemplateList({
  onNew,
  onEdit,
  onSend
}: {
  onNew: () => void;
  onEdit: (t: MarketingTemplate) => void;
  onSend: (t: MarketingTemplate) => void;
}) {
  const { data, isLoading } = useMarketingTemplates();
  const del = useDeleteTemplate();

  return (
    <FormSection title="Your templates">
      <div className="space-y-3">
        {isLoading && <p className="text-sm text-slate-500">Loading…</p>}
        {data && data.length === 0 && (
          <p className="text-sm text-slate-500">No templates yet. Create one to get started.</p>
        )}
        {data?.map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-4 py-3">
            <div className="min-w-0">
              <div className="font-medium text-ink truncate">{t.name}</div>
              <div className="text-sm text-slate-500 truncate">{t.subject}</div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button variant="ghost" onClick={() => onEdit(t)}>Edit</Button>
              <Button
                variant="ghost"
                onClick={() => {
                  if (confirm(`Delete “${t.name}”?`)) del.mutate(t.id);
                }}
              >
                Delete
              </Button>
              <Button onClick={() => onSend(t)}>Send</Button>
            </div>
          </div>
        ))}
        <div className="pt-1">
          <Button variant="secondary" onClick={onNew}>New template</Button>
        </div>
      </div>
    </FormSection>
  );
}

function TemplateEditor({ template, onDone }: { template?: MarketingTemplate; onDone: () => void }) {
  const save = useSaveTemplate();
  const [form, setForm] = useState<UpsertTemplate>({
    name: template?.name ?? '',
    subject: template?.subject ?? '',
    bodyHtml: template?.bodyHtml ?? ''
  });

  const set = (k: keyof UpsertTemplate) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  const canSave = form.name.trim() && form.subject.trim() && form.bodyHtml.trim();

  const onSave = async () => {
    await save.mutateAsync({ id: template?.id, body: form });
    onDone();
  };

  return (
    <FormSection title={template ? 'Edit template' : 'New template'}>
      <div className="space-y-4">
        <FieldRow label="Name (only you see this)" htmlFor="mt-name">
          <input id="mt-name" className={controlClass} value={form.name} onChange={set('name')} />
        </FieldRow>
        <FieldRow label="Subject line" htmlFor="mt-subj">
          <input id="mt-subj" className={controlClass} value={form.subject} onChange={set('subject')} />
        </FieldRow>
        <FieldRow label="Message" htmlFor="mt-body">
          <textarea
            id="mt-body"
            className={`${controlClass} min-h-[160px] font-mono text-xs`}
            value={form.bodyHtml}
            onChange={set('bodyHtml')}
            placeholder="<p>Hi there! We have openings next week…</p>"
          />
        </FieldRow>
        <div>
          <div className="text-xs font-medium text-slate-500 mb-1">Preview</div>
          <div
            className="rounded-lg border border-slate-200 p-4 text-sm bg-white"
            // Tenant's own content; sanitized server-side on save.
            dangerouslySetInnerHTML={{ __html: form.bodyHtml || '<span style="color:#94a3b8">Nothing yet</span>' }}
          />
          <p className="text-xs text-slate-400 mt-2">
            Your business name, address, and an unsubscribe link are added automatically to every send.
          </p>
        </div>
        {save.isError && <p className="text-sm text-danger">Couldn’t save — please try again.</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onDone}>Cancel</Button>
          <Button disabled={!canSave || save.isPending} onClick={onSave}>
            {save.isPending ? 'Saving…' : 'Save template'}
          </Button>
        </div>
      </div>
    </FormSection>
  );
}

function SendPanel({ template, onDone }: { template: MarketingTemplate; onDone: () => void }) {
  const { data, isLoading } = useMarketingEligibility();
  const send = useSendMarketing();
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [result, setResult] = useState<{ count: number; remaining: number } | null>(null);

  // Default selection = all eligible recipients, once loaded.
  const selectedSet = useMemo(() => {
    if (selected) return selected;
    if (!data) return new Set<string>();
    return new Set(data.recipients.filter((r) => r.eligible).map((r) => r.customerId));
  }, [selected, data]);

  const toggle = (id: string) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  if (isLoading || !data) {
    return <FormSection title={`Send “${template.name}”`}><p className="text-sm text-slate-500">Loading recipients…</p></FormSection>;
  }

  if (!data.addressOnFile) {
    return (
      <FormSection title={`Send “${template.name}”`}>
        <p className="text-sm text-ink">
          Add your business mailing address before sending marketing email — it’s legally required in the footer.
        </p>
        <div className="mt-3 flex gap-2">
          <Button variant="secondary" onClick={onDone}>Back</Button>
          <Link to="/settings"><Button>Add address</Button></Link>
        </div>
      </FormSection>
    );
  }

  if (result) {
    return (
      <FormSection title="Sent">
        <p className="text-sm text-ink">
          Your message is on its way to <strong>{result.count}</strong> client{result.count === 1 ? '' : 's'}.
        </p>
        <p className="text-sm text-slate-500 mt-1">{result.remaining} of {data.dailyCap} left to send today.</p>
        <div className="mt-4"><Button onClick={onDone}>Done</Button></div>
      </FormSection>
    );
  }

  const selectedCount = selectedSet.size;
  const overQuota = selectedCount > data.remaining;

  const onSend = async () => {
    const res = await send.mutateAsync({ templateId: template.id, customerIds: [...selectedSet] });
    setResult({ count: res.recipientCount, remaining: res.remainingToday });
  };

  return (
    <FormSection title={`Send “${template.name}”`}>
      <div className="space-y-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">Subject: <span className="text-ink">{template.subject}</span></span>
          <span className={overQuota ? 'text-danger font-medium' : 'text-slate-500'}>
            {selectedCount} selected · {data.remaining}/{data.dailyCap} left today
          </span>
        </div>

        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-80 overflow-y-auto">
          {data.recipients.map((r) => (
            <label
              key={r.customerId}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm ${
                r.eligible ? 'cursor-pointer hover:bg-slate-50' : 'opacity-50'
              }`}
            >
              <input
                type="checkbox"
                disabled={!r.eligible}
                checked={r.eligible && selectedSet.has(r.customerId)}
                onChange={() => toggle(r.customerId)}
              />
              <span className="flex-1 min-w-0">
                <span className="text-ink">{r.displayName}</span>
                {r.email && <span className="text-slate-400"> · {r.email}</span>}
              </span>
              {!r.eligible && r.exclusionReason && (
                <span className="text-xs text-slate-400">{REASON_LABEL[r.exclusionReason] ?? r.exclusionReason}</span>
              )}
            </label>
          ))}
          {data.recipients.length === 0 && (
            <p className="px-4 py-3 text-sm text-slate-500">No active clients yet.</p>
          )}
        </div>

        {overQuota && (
          <p className="text-sm text-danger">
            You can send to {data.remaining} more today. Unselect a few, or send the rest tomorrow.
          </p>
        )}
        {send.isError && <p className="text-sm text-danger">Couldn’t send — please try again.</p>}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onDone}>Cancel</Button>
          <Button disabled={selectedCount === 0 || overQuota || send.isPending} onClick={onSend}>
            {send.isPending ? 'Sending…' : `Send to ${selectedCount}`}
          </Button>
        </div>
      </div>
    </FormSection>
  );
}
